import React, { useEffect, useState } from 'react';
import { usePreferences } from './state/usePreferences';
import { useDevice } from '../hooks/useDevice';
import { useProgress } from './hooks/useProgress';
import ClosedNotice from './components/ClosedNotice';
import Loader from '../Loader/Loader';
import { logoUrl } from './runtime';
import DeadlineCard from './components/DeadlineCard';
import ProgressBand from './components/ProgressBand';
import BottomBar from './components/BottomBar';
import SubmitGate from './components/SubmitGate';
import Section from './sections/Section';
import SubmitCard from './sections/SubmitCard';

/**
 * The form itself, rendered from whatever the store holds. It does not know
 * where the payload came from: the client's brief and the ops preview both
 * bootstrap into the same slice. `banner` is the one thing the caller owns.
 */
const Shell = ({
  banner,
  navSelector = '.navbar',
  staff
}) => {
  const {
    doc,
    loading,
    locked,
    readOnly,
    canEdit,
    denied,
    deniedCode,
    round,
    roundDate,
    section,
    nextKey,
    saveState
  } = usePreferences();
  const {
    positionLabel
  } = useProgress();
  const {
    isPhone,
    narrow
  } = useDevice();
  const [gateOpen, setGateOpen] = useState(false);

  // The app Navbar has an auto height, so measure it rather than hardcoding an
  // offset that drifts between EG and WG chrome, and between the two apps.
  useEffect(() => {
    // Marks the page while the form is open, so the form's own sheet can reach
    // the app's navbar without changing it for every other route.
    document.body.classList.add('cp-form-open');
    const nav = document.querySelector(navSelector);
    const sync = () => {
      if (nav) document.documentElement.style.setProperty('--cp-nav', `${nav.offsetHeight}px`);
    };
    sync();
    // The navbar's own height changes without the window resizing, on phone
    // where its items wrap, and a stale offset leaves a strip above the band.
    const observer = nav && window.ResizeObserver ? new ResizeObserver(sync) : null;
    if (observer) observer.observe(nav);
    window.addEventListener('resize', sync);
    return () => {
      document.body.classList.remove('cp-form-open');
      if (observer) observer.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [navSelector]);
  if (loading) return React.createElement(Loader, {
    logoSrc: logoUrl(),
    label: "Loading your data"
  });
  // Refused by the server, so there is nothing to render but the reason: no
  // nav, no progress, no save state, no submit.
  if (denied) return React.createElement("div", {
    className: "cp-denied"
  }, React.createElement("div", {
    className: "cp-denied-box"
  }, React.createElement("svg", {
    className: "cp-denied-art",
    width: "26",
    height: "26",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  }, React.createElement("rect", {
    x: "3",
    y: "11",
    width: "18",
    height: "11",
    rx: "2"
  }), React.createElement("path", {
    d: "M7 11V7a5 5 0 0 1 10 0v4"
  })), deniedCode === 'brief_not_assigned' ? React.createElement(React.Fragment, null, React.createElement("h1", {
    className: "cp-denied-head"
  }, "You are not assigned to this booking"), React.createElement("p", {
    className: "cp-denied-body"
  }, "This creative preferences form is visible only to the crew assigned to the booking."), React.createElement("ul", {
    className: "cp-denied-points"
  }, React.createElement("li", null, "A professional can open a form only for the bookings they are assigned to."), React.createElement("li", null, "Assignments are set by your EventGraphia manager on the booking itself."), React.createElement("li", null, "If you should be on this shoot, contact your EventGraphia manager to be assigned to it."))) : deniedCode === 'no_published_form' ? React.createElement(React.Fragment, null, React.createElement("h1", {
    className: "cp-denied-head"
  }, "This form is not ready yet"), React.createElement("ul", {
    className: "cp-denied-points"
  }, React.createElement("li", null, "The creative preferences form has not been published yet."), React.createElement("li", null, "Please check again later, or contact your EventGraphia manager."))) : React.createElement(React.Fragment, null, React.createElement("h1", {
    className: "cp-denied-head"
  }, "Only the couple can open this"), React.createElement("ul", {
    className: "cp-denied-points"
  }, React.createElement("li", null, "This creative preferences form is visible only to the bride and groom of the booking."), React.createElement("li", null, "If you think you should have access, contact your EventGraphia manager.")))));
  return React.createElement("div", {
    className: `cp-screen${isPhone ? ' is-phone' : ''}${narrow ? ' is-narrow' : ''}${locked ? ' is-locked' : ''}`
  }, React.createElement(ProgressBand, {
    isPhone: isPhone
  }), React.createElement("div", {
    className: "cp-content"
  }, staff ? null : React.createElement(DeadlineCard, null), banner, staff ? null : React.createElement(ClosedNotice, null), round !== null && round !== undefined ? React.createElement("div", {
    className: "cp-savewarn is-info"
  }, "Version ", round, roundDate ? `, submitted ${roundDate}` : '', ". This is a past version and cannot be edited.") : readOnly && (canEdit === false && !staff ? React.createElement("div", {
    className: "cp-savewarn is-info"
  }, "Only the bride and groom can fill this form. You have view-only access to this booking.") : React.createElement("div", {
    className: "cp-savewarn is-info"
  }, "Read-only view of the client's form.")), saveState === 'error' && React.createElement("div", {
    className: "cp-savewarn"
  }, "Not saved yet — retrying."), saveState === 'rejected' && React.createElement("div", {
    className: "cp-savewarn"
  }, "We could not save that. Please refresh, or contact your EventGraphia manager."), React.createElement(Section, {
    section: section,
    isPhone: isPhone,
    isLast: !!section && !nextKey,
    footer: section && !nextKey ? React.createElement(SubmitCard, {
      onOpenSubmit: () => setGateOpen(true)
    }) : null
  })), React.createElement(BottomBar, null), React.createElement(SubmitGate, {
    open: gateOpen,
    isPhone: isPhone,
    onClose: () => setGateOpen(false),
    onSubmit: () => setGateOpen(false)
  }), React.createElement("div", {
    className: "cp-sr-live",
    "aria-live": "polite"
  }, doc.submitted ? 'Creative preferences submitted.' : positionLabel));
};
export default Shell;
